import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Typography,
    Collapse,
    styled,
    useTheme,
    Chip
} from '@mui/material';
import { tableCellClasses } from '@mui/material/TableCell';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import LoadingIndicator from './LoadingIndicator';
import PlanoAcaoResultadoConsulta from '../types/negocio/PlanoAcaoResultadoConsulta';
import { Desdobramento } from '../types/negocio/Desdobramento';
import { planoAcaoService } from '../services/PlanoAcaoService';
import { desdobramentoService } from '../services/DesdobramentoService';
import Meta from '../types/negocio/Meta';
import { metaService } from '../services/MetaService';
import { colaboradorService } from '../services/ColaboradorService';
import Colaborador from '../types/negocio/Colaborador';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        fontWeight: 'bold',
        whiteSpace: 'nowrap',
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        verticalAlign: 'top',
    },
}));

const StyledTableCellNested = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: 'white',
        color: theme.palette.primary.main,
        fontWeight: 'bold',
        fontSize: 12,
        padding: '8px 16px',
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 12,
        padding: '8px 16px',
    },
}));

interface PlanoInvestimento extends PlanoAcaoResultadoConsulta {
    desdobramentos: Desdobramento[];
    totalInvestimento: number;
    retornoFinanceiro?: string;
}

interface MetaInvestimento {
    meta: Meta;
    responsavelNome: string;
    status: 'Planejada' | 'Em Andamento' | 'Finalizada';
    totalOrcado: number;
    totalRealizado: number;
}

function Row({ plano }: { plano: PlanoInvestimento }) {
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    const prazoFormatado = plano.prazoConclusao ? format(new Date(plano.prazoConclusao), 'dd/MM/yyyy', { locale: ptBR }) : '';

    const getStatusColor = (status?: string) => {
        switch (status?.toLowerCase()) {
            case 'concluído':
            case 'concluido':
            case 'finalizada':
                return 'success';
            case 'em andamento':
            case 'em_andamento':
                return 'warning';
            case 'pausado':
            case 'cancelado':
                return 'error';
            default:
                return 'default';
        }
    };

    const formatCurrency = (value?: string) => {
        if (!value) return 'R$ 0,00';
        const numValue = parseFloat(value);
        return `R$ ${numValue.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`;
    };

    return (
        <React.Fragment>
            <TableRow>
                <StyledTableCell>
                    <IconButton size="small" onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </StyledTableCell>
                <StyledTableCell>{plano.acao}</StyledTableCell>
                <StyledTableCell>{prazoFormatado || '-'}</StyledTableCell>
                <StyledTableCell>{plano.responsavelNome ?? '-'}</StyledTableCell>
                <StyledTableCell>
                    <Chip 
                        label={plano.status || 'Não definido'} 
                        color={getStatusColor(plano.status)}
                        size="small"
                    />
                </StyledTableCell>
                <StyledTableCell>
                    <Typography variant="body2" fontWeight="bold" color="primary">
                        {formatCurrency(plano.totalInvestimento.toString())}
                    </Typography>
                </StyledTableCell>
                <StyledTableCell>
                    <Typography variant="body2" fontWeight="bold" color="success.main">
                        {plano.retornoFinanceiro ? formatCurrency(plano.retornoFinanceiro) : '-'}
                    </Typography>
                </StyledTableCell>
            </TableRow>

            <TableRow>
                <TableCell colSpan={7} sx={{ p: 0 }}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box
                            sx={{
                                p: 2,
                                backgroundColor: theme.palette.mode === 'light' ? '#f9f9f9' : theme.palette.background.paper,
                                borderTop: `1px solid ${theme.palette.divider}`,
                            }}
                        >
                            {plano.desdobramentos.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                    Nenhum desdobramento encontrado para esta ação.
                                </Typography>
                            ) : (
                                <TableContainer component={Paper} elevation={1}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <StyledTableCellNested sx={{ width: '25%' }}>Tarefa</StyledTableCellNested>
                                                <StyledTableCellNested sx={{ width: '25%' }}>Importância</StyledTableCellNested>
                                                <StyledTableCellNested sx={{ width: '20%' }}>Responsável</StyledTableCellNested>
                                                <StyledTableCellNested sx={{ width: '15%' }}>Prazo</StyledTableCellNested>
                                                <StyledTableCellNested sx={{ width: '15%' }}>Valor</StyledTableCellNested>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {plano.desdobramentos.map((desdobramento, index) => (
                                                <TableRow key={index}>
                                                    <StyledTableCellNested>
                                                        {desdobramento.oque || '-'}
                                                    </StyledTableCellNested>
                                                    <StyledTableCellNested>
                                                        {desdobramento.porQue || '-'}
                                                    </StyledTableCellNested>
                                                    <StyledTableCellNested>
                                                        {desdobramento.quemNomeCompleto || '-'}
                                                    </StyledTableCellNested>
                                                    <StyledTableCellNested>
                                                        {desdobramento.quando || '-'}
                                                    </StyledTableCellNested>
                                                    <StyledTableCellNested>
                                                        {formatCurrency(desdobramento.quantoCusta)}
                                                    </StyledTableCellNested>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
}

const TabelaInvestimento: React.FC = () => {
    const [planos, setPlanos] = useState<PlanoInvestimento[]>([]);
    const [metasFinanceiras, setMetasFinanceiras] = useState<MetaInvestimento[]>([]);
    const [loading, setLoading] = useState(false);
    const theme = useTheme();

    const calcularTotalInvestimento = (desdobramentos: Desdobramento[]): number => {
        return desdobramentos.reduce((total, desdobramento) => {
            const valor = parseFloat(desdobramento.quantoCusta || '0');
            return total + (isNaN(valor) ? 0 : valor);
        }, 0);
    };

    const calcularTotalRetornoFinanceiro = (planos: PlanoInvestimento[]): number => {
        return planos.reduce((total, plano) => {
            const valor = parseFloat(plano.retornoFinanceiro || '0');
            return total + (isNaN(valor) ? 0 : valor);
        }, 0);
    };

    const calcularStatusMeta = (meta: Meta): 'Planejada' | 'Em Andamento' | 'Finalizada' => {
        const percentual = meta.totalOrcado && meta.totalOrcado > 0 
            ? ((meta.totalRealizado || 0) / meta.totalOrcado) * 100 
            : 0;
        
        if (percentual === 0) return 'Planejada';
        if (percentual >= 100) return 'Finalizada';
        return 'Em Andamento';
    };

    const fetchDados = async () => {
        try {
            setLoading(true);
            
            // Buscar planos de ação, metas e colaboradores em paralelo
            const [planosResponse, metasResponse, colaboradoresResponse] = await Promise.all([
                planoAcaoService.buscarPlanos(),
                metaService.listarTodos(),
                colaboradorService.listarTodos()
            ]);
            
            // Processar planos de ação
            if (!Array.isArray(planosResponse)) {
                console.error('Resposta inesperada ao carregar planos:', planosResponse);
                setPlanos([]);
            } else {
                const planosComInvestimento: PlanoInvestimento[] = await Promise.all(
                    planosResponse.map(async (plano) => {
                        try {
                            const desdobramentos = plano.guid 
                                ? await desdobramentoService.listarPorPlano(plano.guid, {})
                                : [];

                            const totalInvestimento = calcularTotalInvestimento(desdobramentos || []);

                            return {
                                ...plano,
                                desdobramentos: desdobramentos || [],
                                totalInvestimento,
                                retornoFinanceiro: plano.retornoFinanceiro
                            };
                        } catch (error) {
                            console.error(`Erro ao carregar desdobramentos para plano ${plano.guid}:`, error);
                            return {
                                ...plano,
                                desdobramentos: [],
                                totalInvestimento: 0,
                                retornoFinanceiro: plano.retornoFinanceiro
                            };
                        }
                    })
                );
                setPlanos(planosComInvestimento);
            }

            // Processar metas financeiras (tipo === 'Valor')
            if (Array.isArray(metasResponse)) {
                const colaboradores: Colaborador[] = Array.isArray(colaboradoresResponse) ? colaboradoresResponse : [];
                const metasValor = metasResponse
                    .filter((m: Meta) => m.tipo === 'Valor')
                    .map((meta: Meta): MetaInvestimento => {
                        const responsavel = colaboradores.find(c => c.guid === meta.responsavelGuid);
                        return {
                            meta,
                            responsavelNome: responsavel?.nomeCompleto || '-',
                            status: calcularStatusMeta(meta),
                            totalOrcado: meta.totalOrcado || 0,
                            totalRealizado: meta.totalRealizado || 0,
                        };
                    });
                setMetasFinanceiras(metasValor);
            }
        } catch (error) {
            console.error('Erro ao carregar dados de investimento:', error);
            setPlanos([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDados();
    }, []);

    const totalGeral = planos.reduce((total, plano) => total + plano.totalInvestimento, 0);
    const totalRetornoPlanos = calcularTotalRetornoFinanceiro(planos);
    const totalRealizadoMetas = metasFinanceiras.reduce((total, m) => total + m.totalRealizado, 0);
    const totalRetornoFinanceiro = totalRetornoPlanos + totalRealizadoMetas;

    const formatCurrency = (value: number) => {
        return `R$ ${value.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`;
    };

    if (loading) {
        return <LoadingIndicator message="Carregando dados de investimento..." />;
    }

    return (
        <Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5" fontWeight="medium">
                    Análise de Investimento
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            px: 3,
                            py: 1.5,
                            borderRadius: 3,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.primary.main}08)`,
                            border: `2px solid ${theme.palette.primary.main}20`,
                            backdropFilter: 'blur(10px)',
                        }}
                    >
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            Valor Investido
                        </Typography>
                        <Typography 
                            variant="h6" 
                            fontWeight="bold" 
                            color="primary.main"
                            sx={{ 
                                fontSize: '1.25rem',
                                letterSpacing: '0.5px'
                            }}
                        >
                            {formatCurrency(totalGeral)}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            px: 3,
                            py: 1.5,
                            borderRadius: 3,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.primary.main}08)`,
                            border: `2px solid ${theme.palette.primary.main}20`,
                            backdropFilter: 'blur(10px)',
                        }}
                    >
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            Valor Retorno
                        </Typography>
                        <Typography 
                            variant="h6" 
                            fontWeight="bold" 
                            color="primary.main"
                            sx={{ 
                                fontSize: '1.25rem',
                                letterSpacing: '0.5px'
                            }}
                        >
                            {formatCurrency(totalRetornoFinanceiro)}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Paper
                elevation={3}
                sx={{
                    borderRadius: '8px',
                    boxShadow: theme.shadows[2],
                    overflow: 'hidden',
                    backgroundColor: theme.palette.background.paper
                }}
            >
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell />
                                <StyledTableCell sx={{ width: '36%' }}>Ação</StyledTableCell>
                                <StyledTableCell sx={{ width: '10%' }}>Prazo</StyledTableCell>
                                <StyledTableCell sx={{ width: '20%' }}>Responsável</StyledTableCell>
                                <StyledTableCell sx={{ width: '10%' }}>Status</StyledTableCell>
                                <StyledTableCell sx={{ width: '12%' }}>Investido</StyledTableCell>
                                <StyledTableCell sx={{ width: '12%' }}>Retorno</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {planos.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography variant="body1">Nenhum plano de ação encontrado.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                planos.map((plano, index) => (
                                    <Row key={plano.guid ?? index} plano={plano} />
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Tabela de Metas Financeiras */}
            <Box sx={{ mt: 4 }}>
                <Typography variant="h6" fontWeight="medium" sx={{ mb: 2 }}>
                    Metas Específicas
                </Typography>
                <Paper
                    elevation={3}
                    sx={{
                        borderRadius: '8px',
                        boxShadow: theme.shadows[2],
                        overflow: 'hidden',
                        backgroundColor: theme.palette.background.paper
                    }}
                >
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell sx={{ width: '35%' }}>Meta</StyledTableCell>
                                    <StyledTableCell sx={{ width: '20%' }}>Responsável</StyledTableCell>
                                    <StyledTableCell sx={{ width: '15%' }}>Status</StyledTableCell>
                                    <StyledTableCell sx={{ width: '15%' }}>Orçado</StyledTableCell>
                                    <StyledTableCell sx={{ width: '15%' }}>Realizado</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {metasFinanceiras.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                                            <Typography variant="body1">Nenhuma meta financeira encontrada.</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    metasFinanceiras.map((metaInv, index) => (
                                        <TableRow key={metaInv.meta.guid ?? `meta-${index}`}>
                                            <StyledTableCell>{metaInv.meta.meta}</StyledTableCell>
                                            <StyledTableCell>{metaInv.responsavelNome}</StyledTableCell>
                                            <StyledTableCell>
                                                <Chip
                                                    label={metaInv.status}
                                                    color={
                                                        metaInv.status === 'Finalizada' ? 'success' :
                                                        metaInv.status === 'Em Andamento' ? 'warning' : 'default'
                                                    }
                                                    size="small"
                                                />
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                <Typography variant="body2" fontWeight="bold" color="text.secondary">
                                                    {formatCurrency(metaInv.totalOrcado)}
                                                </Typography>
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                <Typography variant="body2" fontWeight="bold" color="success.main">
                                                    {formatCurrency(metaInv.totalRealizado)}
                                                </Typography>
                                            </StyledTableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>
        </Box>
    );
};

export default TabelaInvestimento;